import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ProcessoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [processo, setProcesso] = useState(null);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    data_movimentacao: new Date().toISOString().split('T')[0],
    descricao: '',
    tipo: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [processoRes, movimentacoesRes] = await Promise.all([
        api.getProcesso(id),
        api.getMovimentacoes(id)
      ]);
      setProcesso(processoRes.data);
      setMovimentacoes(movimentacoesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createMovimentacao(id, formData);
      toast.success('Movimentação registrada com sucesso!');
      setIsDialogOpen(false);
      setFormData({
        data_movimentacao: new Date().toISOString().split('T')[0],
        descricao: '',
        tipo: ''
      });
      loadData();
    } catch (error) {
      console.error('Erro ao registrar movimentação:', error);
      toast.error('Erro ao registrar movimentação');
    }
  };

  const handleConsultarTribunal = async () => {
    try {
      const response = await api.getTribunalInfo(id);
      const { url, tribunal, sistema } = response.data;
      if (url) {
        window.open(url, '_blank');
        toast.success(`Abrindo ${tribunal} - ${sistema}`);
      } else {
        toast.error('Tribunal não identificado');
      }
    } catch (error) {
      console.error('Erro ao consultar tribunal:', error);
      toast.error('Erro ao consultar tribunal');
    }
  };

  if (loading) {
    return <div className="p-8" data-testid="processo-detalhes-loading">Carregando...</div>;
  }

  if (!processo) {
    return <div className="p-8">Processo não encontrado</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="processo-detalhes-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/processos')} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-semibold text-slate-900">{processo.numero_processo}</h1>
          <p className="text-slate-600 mt-1">{processo.tipo_acao}</p>
        </div>
        <Button onClick={handleConsultarTribunal} data-testid="consultar-tribunal-detalhes-button">
          <ExternalLink className="w-4 h-4 mr-2" />
          Consultar no Tribunal
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informações do Processo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-600">Cliente</p>
                <p className="text-slate-900">{processo.cliente?.nome_completo}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Status</p>
                <p className="text-slate-900">{processo.status}</p>
              </div>
              {processo.tribunal && (
                <>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Tribunal</p>
                    <p className="text-slate-900">{processo.tribunal}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Sistema</p>
                    <p className="text-slate-900">{processo.sistema_tribunal}</p>
                  </div>
                </>
              )}
              {processo.valor_causa && (
                <div>
                  <p className="text-sm font-medium text-slate-600">Valor da Causa</p>
                  <p className="text-slate-900">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(processo.valor_causa)}
                  </p>
                </div>
              )}
              {processo.data_distribuicao && (
                <div>
                  <p className="text-sm font-medium text-slate-600">Data de Distribuição</p>
                  <p className="text-slate-900">
                    {new Date(processo.data_distribuicao).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
              {processo.data_limite && (
                <div>
                  <p className="text-sm font-medium text-slate-600">Data Limite</p>
                  <p className="text-slate-900 font-semibold">
                    {new Date(processo.data_limite).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
            {processo.descricao && (
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">Descrição</p>
                <p className="text-slate-900">{processo.descricao}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button className="w-full" variant="outline" onClick={handleConsultarTribunal}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Consultar Andamento
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="add-movimentacao-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Movimentação
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Registrar Movimentação</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="data_movimentacao">Data *</Label>
                    <Input
                      id="data_movimentacao"
                      data-testid="movimentacao-data-input"
                      type="date"
                      required
                      value={formData.data_movimentacao}
                      onChange={(e) => setFormData({ ...formData, data_movimentacao: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="tipo">Tipo</Label>
                    <Input
                      id="tipo"
                      placeholder="Ex: Citação, Audiência, Sentença"
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="descricao">Descrição *</Label>
                    <textarea
                      id="descricao"
                      data-testid="movimentacao-descricao-input"
                      className="w-full min-h-[100px] p-2 border rounded-md"
                      required
                      placeholder="Descreva a movimentação..."
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-movimentacao-button">
                      Registrar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Histórico de Movimentações</CardTitle>
        </CardHeader>
        <CardContent>
          {movimentacoes.length > 0 ? (
            <div className="space-y-4">
              {movimentacoes.map((mov, index) => (
                <div
                  key={mov.id}
                  className="border-l-2 border-slate-200 pl-4 pb-4 relative"
                  data-testid="movimentacao-item"
                >
                  <div className="absolute -left-[9px] top-0 w-4 h-4 bg-slate-900 rounded-full"></div>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">
                        {new Date(mov.data_movimentacao).toLocaleDateString('pt-BR')}
                        {mov.tipo && ` - ${mov.tipo}`}
                      </p>
                      <p className="text-slate-600 mt-1">{mov.descricao}</p>
                      <p className="text-xs text-slate-500 mt-2">
                        Registrado em {new Date(mov.created_at).toLocaleDateString('pt-BR')} às {new Date(mov.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Nenhuma movimentação registrada</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcessoDetalhes;